import {
  type AccountInfo,
  type AuthenticationResult,
  type Configuration,
  type IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";
import { AUTH_BYPASS, getAzureAdConfig } from "../config";

export { AUTH_BYPASS };

const azureConfig = getAzureAdConfig();

export const msalConfig: Configuration = {
  auth: {
    clientId: azureConfig.clientId,
    authority: `https://login.microsoftonline.com/${azureConfig.tenantId}`,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: "localStorage",
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
      logLevel: LogLevel.Warning,
    },
  },
};

let _msalInstance: IPublicClientApplication | null = null;

export function getMsalInstance(): IPublicClientApplication {
  if (!_msalInstance) {
    if (AUTH_BYPASS) {
      _msalInstance = createBypassMsalInstance();
    } else {
      _msalInstance = new PublicClientApplication(msalConfig);
    }
  }
  return _msalInstance;
}

export const apiRequest = {
  scopes: [`api://${azureConfig.clientId}/user_access`],
};

export const graphRequest = {
  scopes: ["User.Read"],
};

function createBypassMsalInstance(): IPublicClientApplication {
  const bypassAccount: AccountInfo = {
    homeAccountId: "bypass-home-id",
    environment: "bypass-env",
    tenantId: "bypass-tenant",
    username: "test@fugro.com",
    localAccountId: "bypass-local-id",
    name: "Test User",
  };

  const bypassTokenResponse: AuthenticationResult = {
    authority: "https://login.microsoftonline.com/common",
    uniqueId: "bypass-unique",
    tenantId: "bypass-tenant",
    scopes: [],
    account: bypassAccount,
    idToken: "bypass-id-token",
    idTokenClaims: {},
    accessToken: "bypass-access-token",
    fromCache: false,
    expiresOn: new Date(Date.now() + 3600 * 1000),
    tokenType: "Bearer",
    correlationId: "bypass-correlation",
  };

  const realForType = new PublicClientApplication({
    auth: { clientId: "00000000-0000-0000-0000-000000000000" },
  });

  return new Proxy(realForType, {
    get(target, prop, receiver) {
      switch (prop) {
        case "initialize":
          return async () => {};
        case "handleRedirectPromise":
          return async () => null;
        case "getActiveAccount":
          return () => bypassAccount;
        case "getAllAccounts":
          return () => [bypassAccount];
        case "acquireTokenSilent":
          return async () => bypassTokenResponse;
        case "acquireTokenPopup":
          return async () => bypassTokenResponse;
        case "acquireTokenRedirect":
          return async () => {};
        case "setActiveAccount":
          return () => {};
        case "clearCache":
          return async () => {};
        case "loginRedirect":
          return async () => {};
        case "loginPopup":
          return async () => bypassTokenResponse;
        case "logoutRedirect":
          return async () => {};
        case "logoutPopup":
          return async () => {};
        case "addEventCallback":
          return () => "bypass-callback-id";
        case "removeEventCallback":
          return () => {};
        case "enableAccountStorageEvents":
          return () => {};
        case "disableAccountStorageEvents":
          return () => {};
        default:
          return Reflect.get(target, prop, receiver);
      }
    },
  });
}
